import type { SummaryGenerationStatus } from '@renderer/hooks/audio/summary-generation'

export type MeetingSummaryPhase =
  | 'live'
  | 'preparing'
  | 'streaming'
  | 'finalized'
  | 'unavailable'
  | 'failed'
  | 'missing'

export type MeetingSummaryState =
  /** Still recording — the summary section isn't rendered at all. */
  | { phase: 'live' }
  /** Ended; generation is in flight (or imminent) with nothing to show yet. */
  | { phase: 'preparing' }
  /**
   * Tokens are arriving. Deliberately carries no text: the section shell reads
   * this state, and one leaf subscribes to the text, so the shell doesn't
   * re-render per token.
   */
  | { phase: 'streaming' }
  | { phase: 'finalized'; text: string }
  /** Generation will never run for this user — don't wait for it. */
  | { phase: 'unavailable'; reason: 'free' | 'local-storage' }
  /**
   * Generation ran and failed, or completed without producing any text. Text
   * keeps anything the user already saw.
   */
  | { phase: 'failed'; reason: 'error' | 'empty'; message: string; text?: string }
  /** Ended, nothing pending, nothing to show. */
  | { phase: 'missing' }

/** This note's generation as the process that owns it reports it. */
export interface SummaryGenerationSnapshot {
  status: SummaryGenerationStatus
  text: string
  error: string | null
  /** The backend refused generation for this account (HTTP 403). */
  unavailable?: boolean
}

export interface MeetingSummaryInput {
  isLive: boolean
  audioNoteId: string | null
  persistedSummary: string | null
  /** Null when no generation is known for this note. */
  generation: SummaryGenerationSnapshot | null
  /** The note was just saved, so a generation is expected even though none has reported yet. */
  expectsGeneration: boolean
  isUsingLocalStorage: boolean
  isFree: boolean
}

const EMPTY_SUMMARY_MESSAGE = 'No summary was generated for this meeting.'

/**
 * Resolves which of the Meeting View's post-meeting summary states to render
 * from the persisted note plus whatever generation state the owning process
 * reports for this exact note (see `legacySummaryGeneration` for main's
 * singleton fields, and `useMeetingSummary` for the renderer-owned store).
 */
export function deriveMeetingSummaryState(input: MeetingSummaryInput): MeetingSummaryState {
  const { isLive, generation } = input

  if (isLive) {
    return { phase: 'live' }
  }

  const streamed = generation?.text.trim() ?? ''
  const text = input.persistedSummary?.trim() || streamed

  if (generation?.status === 'failed' && !generation.unavailable) {
    return {
      phase: 'failed',
      reason: 'error',
      message: generation.error ?? 'Error generating summary.',
      ...(text ? { text } : {}),
    }
  }

  if (generation?.status === 'preparing') {
    return { phase: 'preparing' }
  }
  if (generation?.status === 'streaming') {
    return { phase: 'streaming' }
  }

  // The streamed snapshot bridges the window between the stream finishing and
  // the by-id refetch landing, so the section doesn't flash empty mid-transition.
  if (text) {
    // Deliberately ahead of the unavailable checks: a user who downgraded (or
    // switched to local storage) should still read a summary they already have,
    // not an upsell painted over real content.
    return { phase: 'finalized', text }
  }

  // Generation never starts for these two, so without this escape hatch the
  // expectsGeneration branch would park them in `preparing` forever.
  if (input.isUsingLocalStorage) {
    return { phase: 'unavailable', reason: 'local-storage' }
  }
  if (input.isFree || generation?.unavailable) {
    return { phase: 'unavailable', reason: 'free' }
  }

  // Finished with no error and no text. Nothing more is coming, so offer the
  // retry instead of a skeleton that never resolves.
  if (generation?.status === 'completed') {
    return { phase: 'failed', reason: 'empty', message: EMPTY_SUMMARY_MESSAGE }
  }

  if (input.expectsGeneration) {
    return { phase: 'preparing' }
  }

  return { phase: 'missing' }
}

export interface LegacySummaryInput {
  audioNoteId: string | null
  /** `savedNote.id` from the live audio-note singleton. */
  liveSavedNoteId: string | null
  /** The note main's summary fields belong to; set alongside `summaryPending`. */
  summaryNoteId: string | null
  summaryPending: boolean
  streamingSummary: string
  summaryError: string | null
}

/**
 * Adapts main's legacy summary fields to a per-note generation. Those fields
 * are a singleton that main stamps with `summaryNoteId`, so they count only
 * when this view owns that note. Ownership is not `savedNote`: the Mini clears
 * it on dismiss, and a summary still streaming must survive that (HIG-6151).
 * `savedNote` does say a generation is expected, since main generates on save
 * and the saved-note and summary broadcasts arrive a frame apart.
 */
export function legacySummaryGeneration(
  input: LegacySummaryInput,
): Pick<MeetingSummaryInput, 'generation' | 'expectsGeneration'> {
  const { audioNoteId, summaryNoteId, summaryPending, summaryError, streamingSummary } = input
  const ownsLiveSummary = audioNoteId !== null && summaryNoteId === audioNoteId
  const expectsGeneration = audioNoteId !== null && input.liveSavedNoteId === audioNoteId

  if (!ownsLiveSummary) {
    return { generation: null, expectsGeneration }
  }
  if (summaryError) {
    return { generation: { status: 'failed', text: streamingSummary, error: summaryError }, expectsGeneration }
  }
  if (summaryPending) {
    const status = streamingSummary.trim() ? 'streaming' : 'preparing'
    return { generation: { status, text: streamingSummary, error: null }, expectsGeneration }
  }
  return { generation: { status: 'completed', text: streamingSummary, error: null }, expectsGeneration }
}
